import { useKestrelViewerContext } from 'features/kestrelViewer/types/KestrelViewerInterfaces'
import { MarkupLabelInput } from 'features/kestrelViewer/ui/popupMenu/shared/MarkupLabelInput'
import {
  InputGroup,
  InputHeader,
} from 'features/kestrelViewer/ui/popupMenu/shared/MarkupPopupContainer'
import { useEscapeKey } from 'features/kestrelViewer/ui/popupMenu/shared/useEscapeKey'
import React, { FC, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { KVMeasurement } from 'types'

import { MeasurementSegmentsList } from './MeasurementSegmentsList'

const MeasurementSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: left;
  width: 100%;
  height: 150px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 5px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`

export const MeasurementMenu: FC<{
  selectedMeasurement: KVMeasurement
}> = ({ selectedMeasurement }) => {
  const { setSelectedMarkup, updateMeasurement } = useKestrelViewerContext()
  const labelInputRef = useRef<HTMLInputElement>(null)
  const [localLabel, setLocalLabel] = useState(selectedMeasurement.label)

  // Handle escape key to close popup
  useEscapeKey(() => setSelectedMarkup(undefined))

  // Focus on label input when popup opens
  useEffect(() => {
    if (labelInputRef.current) {
      labelInputRef.current.focus()
    }
  }, [])

  const handleTabKey = (
    e: React.KeyboardEvent,
    targetRef: React.RefObject<HTMLElement | null>
  ) => {
    if (e.key === 'Tab') {
      e.preventDefault()
      if (targetRef.current) {
        targetRef.current.focus()
      }
    }
  }

  const handleLabelEnter = () => {
    if (!localLabel || localLabel.trim().length === 0) return
    updateMeasurement(
      selectedMeasurement,
      'measurementLabel',
      (m) => {
        m.label = localLabel
        return m
      },
      localLabel
    )
  }

  const handleLabelEscape = () => {
    setLocalLabel(selectedMeasurement.label)
  }

  return (
    <>
      <InputGroup>
        <InputHeader>Label</InputHeader>
        <MarkupLabelInput
          ref={labelInputRef}
          value={localLabel}
          onChange={setLocalLabel}
          placeholder='New Measurement'
          onEnter={handleLabelEnter}
          onEscape={handleLabelEscape}
          onTabKey={(e) => handleTabKey(e, labelInputRef)}
          saveOnBlur={true}
          requireNonEmpty={true}
          warningText='Label is required.'
        />
      </InputGroup>
      <InputGroup>
        <InputHeader>Segments</InputHeader>
        <MeasurementSection>
          <MeasurementSegmentsList selectedMeasurement={selectedMeasurement} />
        </MeasurementSection>
      </InputGroup>
    </>
  )
}
